import PageHero from "@/components/PageHero";
import CTA from "@/components/CTA";
import { motion } from "framer-motion";
import { useState } from "react";
import { Search } from "lucide-react";

const CATEGORIES = ["All", "Dental Labs", "Workflow", "Digital Dentistry", "Business", "Product Updates"];

const ARTICLES = [
  { title: "The digital-first dental lab, decoded", cat: "Dental Labs", read: "6 min read", tint: 0 },
  { title: "Case velocity: the metric that actually matters", cat: "Workflow", read: "5 min read", tint: 1 },
  { title: "How dental groups scale to 50+ locations", cat: "Business", read: "8 min read", tint: 2 },
  { title: "STL, DCM, PLY: what your lab needs to know", cat: "Digital Dentistry", read: "7 min read", tint: 3 },
  { title: "Driver routes: the hidden lever in delivery SLAs", cat: "Workflow", read: "5 min read", tint: 4 },
  { title: "Rxn3D · Timeline v2 & Driver Routes", cat: "Product Updates", read: "3 min read", tint: 5 },
  { title: "From paper Rx to structured cases in 30 days", cat: "Dental Labs", read: "9 min read", tint: 6 },
  { title: "A pricing model your CFO will love", cat: "Business", read: "4 min read", tint: 7 },
  { title: "The case for HIPAA-conscious workflows", cat: "Digital Dentistry", read: "6 min read", tint: 8 },
  { title: "Rxn3D · Reports 2.0", cat: "Product Updates", read: "4 min read", tint: 9 },
];

export default function Resources() {
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("All");
  const filtered = ARTICLES.filter(
    (a) =>
      (cat === "All" || a.cat === cat) &&
      a.title.toLowerCase().includes(q.toLowerCase())
  );

  return (
    <>
      <PageHero
        chapter="§ 08"
        eyebrow="Resources"
        title={"Notes on the / modern dental lab."}
        description="Playbooks, essays and product updates for teams building the future of digital dentistry."
      />

      <section data-testid="resources-toolbar" className="pb-4">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex flex-col md:flex-row md:items-center gap-4 md:gap-8">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              data-testid="resources-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search articles…"
              className="w-full pl-11 pr-4 py-3 rounded-full hairline glass bg-transparent placeholder:text-muted-foreground text-sm outline-none focus:ring-2 focus:ring-brand-plum/40"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                onClick={() => setCat(c)}
                data-testid={`resources-cat-${c.toLowerCase().replace(/\s+/g, "-")}`}
                className={`text-xs px-3 py-1.5 rounded-full transition-colors ${
                  cat === c
                    ? "bg-foreground text-background"
                    : "hairline text-muted-foreground hover:text-foreground"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="resources-grid" className="py-14">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((a, i) => (
            <motion.article
              key={a.title}
              data-testid={`resource-card-${i}`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: (i % 3) * 0.06 }}
              whileHover={{ y: -4 }}
              className="group rounded-3xl hairline glass overflow-hidden"
            >
              <div className="aspect-[16/9] relative overflow-hidden">
                <ArtCover index={a.tint} />
                <div className="absolute top-3 left-3 text-[10px] tracking-[0.2em] uppercase font-mono px-2.5 py-1 rounded-full glass hairline">
                  {a.cat}
                </div>
              </div>
              <div className="p-6">
                <h3 className="font-heading text-lg font-semibold tracking-tight leading-tight">
                  {a.title}
                </h3>
                <div className="mt-3 text-xs text-muted-foreground">{a.read}</div>
              </div>
            </motion.article>
          ))}
          {filtered.length === 0 && (
            <div className="col-span-full py-24 text-center text-muted-foreground text-sm">
              No articles yet.
            </div>
          )}
        </div>
      </section>

      <CTA />
    </>
  );
}

function ArtCover({ index }) {
  return (
    <svg viewBox="0 0 400 220" className="w-full h-full">
      <defs>
        <linearGradient id={`rc-${index}`} x1="0" x2="1" y1="0" y2="1">
          <stop offset="0%" stopColor="#2AA6DE" />
          <stop offset="55%" stopColor="#82298D" />
          <stop offset="100%" stopColor="#C9539F" />
        </linearGradient>
      </defs>
      <rect width="400" height="220" fill="#0F172A" />
      <circle cx={80 + (index * 40) % 220} cy={110} r="120" fill={`url(#rc-${index})`} opacity="0.65" />
      <circle cx={280 - (index * 30) % 180} cy={70 + (index * 20) % 100} r="60" fill={`url(#rc-${index})`} opacity="0.55" />
      <g opacity="0.9">
        <text x="24" y="200" fontFamily="Outfit" fontWeight="800" fontSize="80" fill="#ffffff" letterSpacing="-3">
          {index + 1}
        </text>
      </g>
    </svg>
  );
}
